
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { questionBankAPI, questionsAPI } from "@/services/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, Upload, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const QuestionBankUpload = () => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = useState(null);
  const [questionType, setQuestionType] = useState("single");
  const [difficulty, setDifficulty] = useState("mixed");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  
  const form = useForm({
    defaultValues: {
      name: "",
      description: "",
      category: "",
      price: 0,
      status: "draft",
    }
  });

  const createBankMutation = useMutation({
    mutationFn: (bankData) => questionBankAPI.create(bankData),
    onSuccess: () => {
      toast.success("Question bank created successfully");
      form.reset();
      queryClient.invalidateQueries({ queryKey: ["questionBanks"] });
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create question bank"
      );
    },
  });

  const uploadQuestionsMutation = useMutation({
    mutationFn: (data) => {
      // Simulating file upload progress - in a real app this would be replaced with actual file upload
      setIsUploading(true);
      setUploadProgress(0);

      const simulateProgress = () => {
        setUploadProgress(prev => {
          if (prev < 95) {
            const increment = Math.random() * 10;
            return Math.min(prev + increment, 95);
          }
          return prev;
        });
      };

      // Simulate progress updates
      const interval = setInterval(simulateProgress, 300);

      return new Promise((resolve) => {
        // Simulate API call delay
        setTimeout(() => {
          clearInterval(interval);
          setUploadProgress(100);

          // Simulate different results based on the selected file
          const results = {
            total: 25,
            successful: 22,
            failed: 3,
            errors: [
              { line: 5, message: "Invalid option format" },
              { line: 12, message: "Missing correct answer" },
              { line: 18, message: "Duplicate question detected" }
            ]
          };

          resolve({ data: results });
        }, 3000);
      });
    },
    onSuccess: (response) => {
      setUploadResults(response.data);
      setIsResultsModalOpen(true);
      setIsUploading(false);
      toast.success(`Successfully uploaded ${response.data.successful} questions`);
      setSelectedFile(null);
    },
    onError: (error) => {
      setIsUploading(false);
      toast.error(
        error.response?.data?.message || "Failed to upload questions"
      );
    },
  });

  const onSubmit = (data) => {
    createBankMutation.mutate(data);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleFileUpload = () => {
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("questionType", questionType);
    formData.append("difficulty", difficulty);

    uploadQuestionsMutation.mutate(formData);
  };

  const downloadTemplate = () => {
    // In a real application, this would download a template file
    // based on the selected question type
    toast.info(`Downloading ${questionType} question template`);
    setIsTemplateModalOpen(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Question Bank Management</h1>
        <p className="text-muted-foreground">
          Create new question banks and upload questions
        </p>
      </div>

      <Tabs defaultValue="create" className="space-y-4">
        <TabsList>
          <TabsTrigger value="create">Create Question Bank</TabsTrigger>
          <TabsTrigger value="upload">Upload Questions</TabsTrigger>
        </TabsList>

        <TabsContent value="create" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Create New Question Bank</CardTitle>
              <CardDescription>
                Fill in the details to create a new question bank that can be purchased by companies
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="name"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Programming Fundamentals" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Category</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a category" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="programming">Programming</SelectItem>
                              <SelectItem value="networking">Networking</SelectItem>
                              <SelectItem value="cybersecurity">Cybersecurity</SelectItem>
                              <SelectItem value="data_science">Data Science</SelectItem>
                              <SelectItem value="cloud_computing">Cloud Computing</SelectItem>
                              <SelectItem value="general">General Knowledge</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="price"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Price ($)</FormLabel>
                          <FormControl>
                            <Input type="number" min="0" step="0.01" {...field} />
                          </FormControl>
                          <FormDescription>
                            Set the price that companies will pay for this question bank
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="status"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Status</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="draft">Draft</SelectItem>
                              <SelectItem value="published">Published</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormDescription>
                            Only published question banks will be visible to companies
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="Detailed description of the question bank content"
                            className="min-h-32"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button type="submit" disabled={createBankMutation.isPending}>
                    {createBankMutation.isPending ? "Creating..." : "Create Question Bank"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="upload" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Upload Questions</CardTitle>
              <CardDescription>
                Upload CSV or Excel files containing questions to add to question banks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Question Type</Label>
                    <RadioGroup 
                      defaultValue="single" 
                      className="mt-2"
                      value={questionType}
                      onValueChange={setQuestionType}
                    >
                      <div className="flex items-start space-x-2 mb-2">
                        <RadioGroupItem value="single" id="single" />
                        <div>
                          <Label htmlFor="single">Single Choice Questions</Label>
                          <p className="text-sm text-muted-foreground">
                            Questions with only one correct answer
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2 mb-2">
                        <RadioGroupItem value="multiple" id="multiple" />
                        <div>
                          <Label htmlFor="multiple">Multiple Choice Questions</Label>
                          <p className="text-sm text-muted-foreground">
                            Questions with multiple correct answers
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <RadioGroupItem value="mixed" id="mixed" />
                        <div>
                          <Label htmlFor="mixed">Mixed Question Types</Label>
                          <p className="text-sm text-muted-foreground">
                            A mix of single and multiple choice questions
                          </p>
                        </div>
                      </div>
                    </RadioGroup>
                  </div>

                  <div>
                    <Label>Difficulty Level</Label>
                    <RadioGroup 
                      defaultValue="mixed" 
                      className="mt-2"
                      value={difficulty}
                      onValueChange={setDifficulty}
                    >
                      <div className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value="easy" id="easy" />
                        <Label htmlFor="easy">Easy</Label>
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value="medium" id="medium" />
                        <Label htmlFor="medium">Medium</Label>
                      </div>
                      <div className="flex items-center space-x-2 mb-2">
                        <RadioGroupItem value="hard" id="hard" />
                        <Label htmlFor="hard">Hard</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="mixed" id="mixed-difficulty" />
                        <Label htmlFor="mixed-difficulty">Mixed Difficulty</Label>
                      </div>
                    </RadioGroup>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>File Upload</Label>
                    <div className="mt-2 border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center">
                      <Upload className="h-10 w-10 text-muted-foreground mb-2" />
                      <p className="mb-2 text-sm font-medium">
                        Drag and drop your file or click to browse
                      </p>
                      <p className="mb-4 text-xs text-muted-foreground">
                        Supported formats: .csv, .xlsx, .xls (max 10MB)
                      </p>
                      <Input
                        id="file-upload"
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={handleFileChange}
                      />
                      <Button asChild>
                        <label htmlFor="file-upload">Choose File</label>
                      </Button>
                      {selectedFile && (
                        <p className="mt-3 text-sm">
                          Selected: <span className="font-medium">{selectedFile.name}</span>
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Button
                      variant="outline"
                      onClick={() => setIsTemplateModalOpen(true)}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" /> Download Template
                    </Button>
                    <Button
                      onClick={handleFileUpload}
                      disabled={!selectedFile || isUploading}
                      className="gap-2"
                    >
                      <Upload className="h-4 w-4" />
                      {isUploading ? "Uploading..." : "Upload"}
                    </Button>
                  </div>

                  {isUploading && (
                    <div className="space-y-2">
                      <Progress value={uploadProgress} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Uploading...</span>
                        <span>{Math.round(uploadProgress)}%</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex flex-col items-start">
              <p className="text-sm text-muted-foreground mb-2">
                <AlertCircle className="h-4 w-4 inline-block mr-1" />
                Make sure your file follows the template format to avoid errors during upload
              </p>
              <p className="text-sm text-muted-foreground">
                Questions will be validated before being added to the system
              </p>
            </CardFooter>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Upload Results Modal */}
      <Dialog open={isResultsModalOpen} onOpenChange={setIsResultsModalOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Upload Results</DialogTitle>
            <DialogDescription>
              Summary of the question upload process
            </DialogDescription>
          </DialogHeader>
          {uploadResults && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-muted-foreground">Total Questions</p>
                  <p className="text-2xl font-bold">{uploadResults.total}</p>
                </div>
                <div className="bg-green-50 dark:bg-green-950 p-4 rounded-lg border border-green-200 dark:border-green-800">
                  <p className="text-sm text-muted-foreground">Successfully Added</p>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {uploadResults.successful}
                  </p>
                </div>
                <div className="bg-red-50 dark:bg-red-950 p-4 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-muted-foreground">Failed</p>
                  <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {uploadResults.failed}
                  </p>
                </div>
              </div>

              {uploadResults.failed > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-2">Error Details</h3>
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Line</TableHead>
                          <TableHead>Error</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uploadResults.errors.map((error, index) => (
                          <TableRow key={index}>
                            <TableCell>{error.line}</TableCell>
                            <TableCell>{error.message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setIsResultsModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Selection Modal */}
      <Dialog open={isTemplateModalOpen} onOpenChange={setIsTemplateModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Download Template</DialogTitle>
            <DialogDescription>
              Select the format for your question template
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <RadioGroup defaultValue="csv">
                <div className="flex items-center space-x-2 mb-2">
                  <RadioGroupItem value="csv" id="csv" />
                  <Label htmlFor="csv">CSV Template</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="excel" id="excel" />
                  <Label htmlFor="excel">Excel Template</Label>
                </div>
              </RadioGroup>
            </div>
            <div className="text-sm">
              <p className="mb-2">The template will include:</p>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Sample questions with the correct format</li>
                <li>Instructions for each column</li>
                <li>Examples for {questionType} choice questions</li>
                <li>Difficulty level indicators</li>
              </ul>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsTemplateModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={downloadTemplate}>Download</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionBankUpload;
